/**
 * Points Calculation Utilities
 *
 * Config-driven scoring system for batting and bowling performance
 * AND squad points calculation with role multipliers (C/VC/X)
 */

import type { BattingConfig, BowlingConfig, FieldingConfig, SquadPlayer, TennisScoringConfig, TennisSquadRules } from '../types/database';

/**
 * Calculate batting points based on runs, balls, and pool's batting config
 *
 * Supports both bonus-only and bonus+penalty modes based on config
 *
 * @param runs - Runs scored
 * @param ballsFaced - Balls faced
 * @param config - Batting configuration from the player pool
 * @returns Calculated batting points (rounded to 2 decimals)
 */
export function calculateBattingPoints(
  runs: number,
  ballsFaced: number,
  config: BattingConfig
): number {
  // If less than minimum threshold, return runs as-is (no bonus/penalty calculation)
  if (ballsFaced < config.minBallsThreshold) {
    return parseFloat(runs.toFixed(2));
  }

  // Calculate Strike Rate
  const SR = (runs / ballsFaced) * 100;

  let strikeRateBonus = 0;

  if (config.penaltiesEnabled && config.penaltySRThreshold) {
    // GENERALIZED MODE: Bonuses AND Penalties
    if (SR >= config.bonusSRTrigger) {
      // Bonus zone: SR meets or exceeds trigger threshold
      strikeRateBonus = (runs * (SR - config.bonusSRBaseline)) / config.bonusDivisor;
    } else if (SR < config.penaltySRThreshold) {
      // Penalty zone: SR below penalty threshold (results in negative bonus)
      strikeRateBonus = (runs * (SR - config.bonusSRBaseline)) / config.bonusDivisor;
    }
    // Neutral zone: between penaltySRThreshold and bonusSRTrigger (no bonus/penalty)
  } else {
    // BONUS-ONLY MODE (No Penalties)
    if (SR >= config.bonusSRTrigger) {
      strikeRateBonus = (runs * (SR - config.bonusSRBaseline)) / config.bonusDivisor;
    }
  }

  // Total points = base runs + SR modifier
  const totalPoints = runs + strikeRateBonus;

  return parseFloat(totalPoints.toFixed(2));
}

/**
 * Calculate bowling points based on overs, runs conceded, wickets, and pool's bowling config
 *
 * Supports both bonus-only and bonus+penalty modes based on config
 *
 * @param overs - Overs bowled (e.g., 3.2 = 3 overs 2 balls)
 * @param runsConceded - Runs conceded
 * @param wickets - Wickets taken
 * @param config - Bowling configuration from the player pool
 * @returns Calculated bowling points (rounded to 2 decimals)
 */
export function calculateBowlingPoints(
  overs: number,
  runsConceded: number,
  wickets: number,
  config: BowlingConfig
): number {
  // Convert overs to balls (e.g., 3.2 = 20 balls)
  const balls = Math.floor(overs) * 6 + (Math.ceil(overs * 10) % 10);

  // Calculate economy rate
  const economy = balls > 0 ? (runsConceded / balls) * 6 : 0;

  // Base wicket points
  const basePoints = wickets * config.wicketPoints;

  // If overs less than or equal to minimum threshold, return wicket points only
  if (overs <= config.minOversForEconomy) {
    return parseFloat(basePoints.toFixed(2));
  }

  let economyBonus = 0;

  if (config.penaltiesEnabled && config.economyPenaltyThreshold) {
    // GENERALIZED MODE: Bonuses AND Penalties
    if (economy < config.economyBonusThreshold) {
      // Bonus zone (good economy)
      economyBonus = (config.economyBonusThreshold - parseFloat(economy.toFixed(1))) * config.economyMultiplier;
    } else if (economy > config.economyPenaltyThreshold) {
      // Penalty zone (poor economy) - results in negative bonus
      economyBonus = (config.economyPenaltyThreshold - parseFloat(economy.toFixed(1))) * config.economyMultiplier;
    }
    // Neutral zone: between bonusThreshold and penaltyThreshold
  } else {
    // BONUS-ONLY MODE (No Penalties)
    if (economy < config.economyBonusThreshold) {
      economyBonus = (config.economyBonusThreshold - parseFloat(economy.toFixed(1))) * config.economyMultiplier;
    }
  }

  // Total points = wicket points + economy modifier
  const totalPoints = basePoints + economyBonus;

  return parseFloat(totalPoints.toFixed(2));
}

/**
 * Default configurations (Current League Rules - No Penalties)
 */
export const DEFAULT_BATTING_CONFIG: BattingConfig = {
  minBallsThreshold: 7,
  bonusSRTrigger: 150,
  bonusSRBaseline: 130,
  bonusDivisor: 200,
  penaltiesEnabled: false,
  penaltySRThreshold: 120, // Not used when penalties disabled
};

export const DEFAULT_BOWLING_CONFIG: BowlingConfig = {
  wicketPoints: 25,
  economyBonusThreshold: 7,
  economyMultiplier: 5,
  penaltiesEnabled: false,
  economyPenaltyThreshold: 8, // Not used when penalties disabled
  minOversForEconomy: 1,
};

export const DEFAULT_FIELDING_CONFIG: FieldingConfig = {
  catchPoints: 5,
  runOutPoints: 5,
  stumpingPoints: 5,
};

/**
 * =============================================================================
 * SQUAD POINTS CALCULATION WITH ROLE MULTIPLIERS
 * =============================================================================
 *
 * CRITICAL: These functions ensure that points are calculated the same way everywhere:
 * - During squad creation
 * - During transfers
 * - During pool updates (recalculateLeaguesUsingPool)
 *
 * Using different formulas in different places causes point instability!
 */

/**
 * Calculate a player's contribution to squad points based on their role
 *
 * This function properly handles:
 * 1. Base points earned before role assignment (1x multiplier)
 * 2. Bonus points earned after role assignment (role-specific multiplier)
 *
 * @param player - The squad player to calculate contribution for
 * @param role - The role this player has ('captain', 'viceCaptain', 'xFactor', or 'regular')
 * @returns The total points this player contributes to the squad
 *
 * @example
 * Player joined at 100 points (pointsAtJoining = 100)
 * Player made VC at 150 points (pointsWhenRoleAssigned = 150)
 * Player now has 200 points (points = 200)
 *
 * Calculation:
 * - Base points: 150 - 100 = 50 (earned as regular, 1x multiplier)
 * - Bonus points: 200 - 150 = 50 (earned as VC, 1.5x multiplier)
 * - Total contribution: 50 * 1.0 + 50 * 1.5 = 50 + 75 = 125 points
 */
export function calculatePlayerContribution(
  player: SquadPlayer,
  role: 'captain' | 'viceCaptain' | 'xFactor' | 'regular'
): number {
  const pointsAtJoining = player.pointsAtJoining ?? 0;
  const pointsWhenRoleAssigned = player.pointsWhenRoleAssigned ?? pointsAtJoining;

  // Base points: earned before role assignment (or all points if no role).
  // Keep signed deltas so penalties/negative points reduce squad totals correctly.
  const basePoints = pointsWhenRoleAssigned - pointsAtJoining;

  // Bonus points: earned after role assignment (only if role assigned).
  // Keep signed deltas so role multipliers apply to both gains and losses.
  const bonusPoints = player.points - pointsWhenRoleAssigned;

  // Apply role-specific multipliers
  if (role === 'captain') {
    // Captain: base at 1x, bonus at 2x
    return basePoints * 1.0 + bonusPoints * 2.0;
  } else if (role === 'viceCaptain') {
    // Vice-Captain: base at 1x, bonus at 1.5x
    return basePoints * 1.0 + bonusPoints * 1.5;
  } else if (role === 'xFactor') {
    // X-Factor: base at 1x, bonus at 1.25x
    return basePoints * 1.0 + bonusPoints * 1.25;
  } else {
    // Regular player: all points at 1x (no multiplier)
    return player.points - pointsAtJoining;
  }
}

/**
 * Calculate total squad points including all role multipliers and banked points
 *
 * IMPORTANT: Only counts starting XI (first squadSize players), excludes bench
 *
 * @param players - Array of squad players (starting XI + bench)
 * @param squadSize - Number of players in starting XI (usually 11)
 * @param captainId - Player ID of the captain (optional)
 * @param viceCaptainId - Player ID of the vice-captain (optional)
 * @param xFactorId - Player ID of the X-factor (optional)
 * @param bankedPoints - Previously accumulated points from transfers (default 0)
 * @returns Object with totalPoints and individual role contributions
 */
export function calculateSquadPoints(
  players: SquadPlayer[],
  squadSize: number,
  captainId: string | undefined,
  viceCaptainId: string | undefined,
  xFactorId: string | undefined,
  bankedPoints: number = 0
): {
  totalPoints: number;
  captainPoints: number;
  viceCaptainPoints: number;
  xFactorPoints: number;
} {
  let totalPoints = 0;
  let captainPoints = 0;
  let viceCaptainPoints = 0;
  let xFactorPoints = 0;

  // CRITICAL: Only count starting XI (first squadSize players), exclude bench
  const startingXI = players.slice(0, squadSize);

  startingXI.forEach(player => {
    // Determine this player's role
    let role: 'captain' | 'viceCaptain' | 'xFactor' | 'regular' = 'regular';

    if (captainId === player.playerId) {
      role = 'captain';
    } else if (viceCaptainId === player.playerId) {
      role = 'viceCaptain';
    } else if (xFactorId === player.playerId) {
      role = 'xFactor';
    }

    // Calculate this player's contribution using shared logic
    const contribution = calculatePlayerContribution(player, role);

    // Track role-specific contributions
    if (role === 'captain') {
      captainPoints = contribution;
    } else if (role === 'viceCaptain') {
      viceCaptainPoints = contribution;
    } else if (role === 'xFactor') {
      xFactorPoints = contribution;
    }

    totalPoints += contribution;
  });

  // Add banked points from previous transfers
  totalPoints += bankedPoints;

  return {
    totalPoints,
    captainPoints,
    viceCaptainPoints,
    xFactorPoints,
  };
}

// =============================================================================
// TENNIS SCORING
// =============================================================================

export const DEFAULT_TENNIS_SCORING_CONFIG: TennisScoringConfig = {
  matchWinPoints: 20,
  straightSetsBonus: 10,
  firstSetLossBonus: 10,
  tieBreakerBonus: 10,
  dominantSetPoints: 10,
  upsetSmallBonus: 8,
  upsetMediumBonus: 16,
  upsetLargeBonus: 24,
  mensPredictionBonus: 20,
  womensPredictionBonus: 20,
  predictionBonus: 20,
};

export const DEFAULT_TENNIS_SQUAD_RULES: TennisSquadRules = {
  menSlots: 5,
  womenSlots: 5,
  menMultipliers: [2, 1.75, 1.5, 1.25, 1],
  womenMultipliers: [2, 1.75, 1.5, 1.25, 1],
  budget: 90,
  maxTransfers: 4,
};

/**
 * Calculate points for a single tennis match performance.
 *
 * @param won - Whether the player won the match
 * @param straightSets - Whether the win was in straight sets (no sets lost)
 * @param finalSetTieBreak - Whether the final set was decided by a tie-break
 * @param setsWonGames - Games won per set by this player (e.g. [6, 6, 6])
 * @param setsLostGames - Games lost per set by this player (e.g. [4, 3, 2])
 * @param playerSeed - Tournament seed of this player (undefined = unseeded)
 * @param opponentSeed - Tournament seed of opponent (undefined = unseeded)
 * @param config - Tennis scoring configuration
 * @returns Total points earned for this match
 */
export function calculateTennisMatchPoints(
  won: boolean,
  straightSets: boolean,
  finalSetTieBreak: boolean,
  setsWonGames: number[],
  setsLostGames: number[],
  playerSeed: number | undefined,
  opponentSeed: number | undefined,
  config: TennisScoringConfig
): number {
  let points = 0;

  // Dominant sets: player won 6 and lost 0 or 1 games in that set.
  // Awarded irrespective of match result — bagelling a set still earns credit even on a loss.
  for (let i = 0; i < setsWonGames.length; i++) {
    const gamesWon = setsWonGames[i];
    const gamesLost = setsLostGames[i] ?? 99;
    if (gamesWon === 6 && gamesLost <= 1) {
      points += config.dominantSetPoints;
    }
  }

  if (!won) return points;

  points += config.matchWinPoints;

  if (straightSets) {
    points += config.straightSetsBonus;
  }

  // First set loss bonus: won match but lost the opening set
  const lostFirstSet = setsWonGames.length > 0 && setsLostGames.length > 0
    && setsLostGames[0] > setsWonGames[0];
  if (lostFirstSet) {
    points += config.firstSetLossBonus ?? 0;
  }

  if (finalSetTieBreak) {
    points += config.tieBreakerBonus;
  }

  // 3-tier upset bonus — only highest applicable tier awarded.
  // Lower seed number = better-ranked player. The bonus goes to the UNDERDOG:
  // the player must have a higher seed number than their opponent (i.e., be
  // ranked worse). `diff > 0` means player is the underdog.
  //
  //   playerSeed=25 beats opponentSeed=1 → diff = 25 − 1 = 24 → large upset
  //   playerSeed=1 beats opponentSeed=25 → diff = 1 − 25 = −24 → no bonus
  if (playerSeed !== undefined && opponentSeed !== undefined) {
    const diff = playerSeed - opponentSeed;
    if (diff >= 20) {
      points += config.upsetLargeBonus;
    } else if (diff >= 10) {
      points += config.upsetMediumBonus ?? 0;
    } else if (diff >= 5) {
      points += config.upsetSmallBonus;
    }
  }

  return points;
}

/**
 * Calculate total points for a tennis squad, applying slot multipliers and prediction bonuses.
 *
 * @param players - Squad players (must have gender and slotIndex set)
 * @param rules - Tennis squad rules defining slots and multipliers
 * @param mensPrediction - Player ID predicted as men's singles winner
 * @param womensPrediction - Player ID predicted as women's singles winner
 * @param mensTournamentWinnerId - Actual men's singles winner player ID
 * @param womensTournamentWinnerId - Actual women's singles winner player ID
 * @param config - Tennis scoring config (for predictionBonus)
 * @returns Total points and breakdown
 */
export function calculateTennisSquadPoints(
  players: SquadPlayer[],
  rules: TennisSquadRules,
  mensPrediction: string | undefined,
  womensPrediction: string | undefined,
  mensTournamentWinnerId: string | undefined,
  womensTournamentWinnerId: string | undefined,
  config: TennisScoringConfig,
  bankedPoints: number = 0
): { totalPoints: number; predictionBonusPoints: number } {
  let totalPoints = 0;
  let predictionBonusPoints = 0;

  for (const player of players) {
    const gender = player.gender;
    const slotIndex = player.slotIndex ?? 0;

    let multiplier = 1;
    if (gender === 'men' && rules.menMultipliers[slotIndex] !== undefined) {
      multiplier = rules.menMultipliers[slotIndex];
    } else if (gender === 'women' && rules.womenMultipliers[slotIndex] !== undefined) {
      multiplier = rules.womenMultipliers[slotIndex];
    }

    const pointsEarned = (player.points - (player.pointsAtJoining ?? 0)) * multiplier;
    totalPoints += pointsEarned;
  }

  // Prediction bonuses — use gender-specific fields, fall back to legacy predictionBonus
  const menBonus = config.mensPredictionBonus ?? config.predictionBonus;
  const womenBonus = config.womensPredictionBonus ?? config.predictionBonus;
  if (mensPrediction && mensTournamentWinnerId && mensPrediction === mensTournamentWinnerId) {
    predictionBonusPoints += menBonus;
  }
  if (womensPrediction && womensTournamentWinnerId && womensPrediction === womensTournamentWinnerId) {
    predictionBonusPoints += womenBonus;
  }

  totalPoints += predictionBonusPoints;
  totalPoints += bankedPoints;

  return { totalPoints, predictionBonusPoints };
}

/**
 * Validate that pointsWhenRoleAssigned is set for players with roles
 *
 * This helps catch data integrity issues where role timestamps are missing
 *
 * @param player - The squad player to validate
 * @param role - The role this player has
 * @returns Validation result with any warnings
 */
export function validateRoleTimestamp(
  player: SquadPlayer,
  role: 'captain' | 'viceCaptain' | 'xFactor' | 'regular'
): { valid: boolean; warning?: string } {
  // Regular players don't need pointsWhenRoleAssigned
  if (role === 'regular') {
    return { valid: true };
  }

  // Players with roles should have pointsWhenRoleAssigned set
  if (player.pointsWhenRoleAssigned === undefined) {
    return {
      valid: false,
      warning: `⚠️ ${player.playerName} is ${role} but missing pointsWhenRoleAssigned. This may cause incorrect point calculations.`,
    };
  }

  // Check if pointsWhenRoleAssigned is reasonable
  const pointsAtJoining = player.pointsAtJoining ?? 0;
  if (player.pointsWhenRoleAssigned < pointsAtJoining) {
    return {
      valid: false,
      warning: `⚠️ ${player.playerName} has pointsWhenRoleAssigned (${player.pointsWhenRoleAssigned}) less than pointsAtJoining (${pointsAtJoining}). Data corruption detected.`,
    };
  }

  return { valid: true };
}
